import { css, html, LitElement } from "lit"
import { classMap } from "lit/directives/class-map.js"
import "@luminecence/composants/BoutonIcone"
import "@luminecence/composants/Badge"
import "@luminecence/composants/Icone"
import PopupBase from "@luminecence/composants/PopupBase"

export const EVENEMENT_BOUTON_SUPPRIMER_POKEMON = "evenementBoutonSupprimerPokemon",
  EVENEMENT_BOUTON_MODIFIER_POKEMON = "evenementBoutonModifierPokemon"

const URL_POKEMON = "https://tyradex.app/api/v1/pokemon",
  BASE_URL_IMAGES = "https://raw.githubusercontent.com/Yarkis01/TyraDex/images/sprites/"

export default class Pokemon extends LitElement {
  static properties = {
    nom: { type: String },
    specificites: { type: Object },
    commentaire: { type: String },
    estModeAmis: { type: Boolean },
    estImageEnErreur: { type: Boolean },
    url: { type: String },
  }

  static styles = css`
        :host {
            display: flex;
            flex-direction: column;
            border-radius: var(--border-radius);
            overflow: hidden;
            padding: 1rem;
            background-color: var(--couleur-200);
        }

        .entete {
            display: flex;
            align-items: center;
            font-size: 2rem;
            font-weight: bold;
            gap: 1rem;
        }

        .entete img {
            width: 2.5rem;
        }

        .entete img.pokemon {
            width: 7rem;
        }

        .entete img.image-erreur {
            border-radius: 50%;
        }

        .entete:has(+ .conteneurInformations) {
          padding-bottom: 1rem;
          border-bottom: 2px solid var(--couleur-300);
        }

        .entete > .nom {
            width: fit-content;
            font-size: 2.4rem;
        }

        .entete > .icones {
            display: flex;
            align-items: center;
            gap: 1.2rem;
            flex: 1;
        }

        .entete > .icones .sexe {
            font-size: 4rem;
            font-weight: bold;
        }

        @media (max-width: 768px) {

          .entete > .sexe {
              font-size: 4rem;
          }
        }

        .actions {
          --icone-taille: 2.2rem;

            display: flex;
            gap: 1.5rem;
            margin-inline-start: auto;
        }

        .conteneurInformations {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            padding-top: 1rem;
        }

        .specificites {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .specificite {
          padding: 0.4rem 1rem;
          border-radius: 50px;
          background-color: var(--couleur-300);
          color: var(--couleur-900);
          white-space: nowrap;
          font-size: 1.2rem;
        }
        `

  constructor() {
    super()
    this.id = ""
    this.nom = ""
    this.specificites = {}
    this.commentaire = ""
    this.estModeAmis = true
    this.estImageEnErreur = false
    this.url = "/images/placeholder_pokemon_inconnu.svg"
  }

  async connectedCallback() {
    super.connectedCallback()
    await this.mettreAJourImage()
  }

  async updated(changedProperties) {
    if (changedProperties.has("nom")) {
      await this.mettreAJourImage()
    }
  }

  async mettreAJourImage() {
    if (!this.nom) {
      this.estImageEnErreur = false
      this.url = "../../images/placeholder_pokemon_inconnu.svg"
      return
    }

    const nomImage = this.nom.toLowerCase().replace(/\s/g, "-").replace(/'/g, "-")
    const idPokemon = window.listePokemons.find((pokemon) => pokemon.name.fr.toLowerCase() === nomImage.toLowerCase())?.pokedex_id

    if (!idPokemon) {
      this.estImageEnErreur = true
      this.url = "../../images/placeholder_pokemon_inconnu.svg"
      return
    }

    this.url = `${BASE_URL_IMAGES}/${idPokemon}/regular.png`
  }

  render() {
    return html`${this.nom ? this.genererPokemon() : ""}`
  }

  genererPokemon() {
    return html`
          <div class="entete">
            ${this.genererImagePokemon()}
              <h2 class="nom">${this.nom}</h2>
              ${this.specificites.sexe ? html`<p-icone .icone=${this.specificites.sexe} title="${this.specificites.sexe}"></p-icone>` : ""}

              ${
                this.estModeAmis
                  ? html``
                  : html`
              <div class="actions">
                  <p-bouton-icone id="editer" icone="editer" @click=${this.modifierPokemon}></p-bouton-icone>
                  <p-bouton-icone id="supprimer" icone="supprimer-pokemon" @click=${this.supprimerPokemon}></p-bouton-icone>
                </div>
                `
              }
          </div>
          ${this.genererConteneurInformations()}
          `
  }

  genererImagePokemon() {
    const fonctionErreurImage = () => {
        this.estImageEnErreur = true
      },
      classes = classMap({
        pokemon: true,
        "image-erreur": this.estImageEnErreur,
      })
    return html`<img src="${this.url}" class=${classes} alt="${this.nom}" @error=${fonctionErreurImage}/>`
  }

  genererConteneurInformations() {
    const listeSpecificites = Object.entries(this.specificites).filter(([, valeur]) => valeur)

    return listeSpecificites.length > 0 || this.commentaire
      ? html`<div class="conteneurInformations">
                    ${listeSpecificites.length > 0 ? html`<div class="specificites">${this.genererSpecificites(listeSpecificites)}</div>` : ""}
                    ${this.commentaire ? html`<span class="commentaire">${this.commentaire}</span>` : ""}
              </div>`
      : ""
  }

  genererSpecificites(listeSpecificites) {
    return listeSpecificites.map(([nomSpecificite, valeur]) => this.genererBadge(nomSpecificite, valeur))
  }

  genererBadge(nomSpecificite, valeur) {
    if (nomSpecificite === "sexe") {
      return ""
    }

    const specificite = this.recupererSpecificite(nomSpecificite, valeur)

    return html`<p-badge texte="${specificite.texte}" .icone=${specificite.icone}></p-badge>`
  }

  /**
   * @param {string} nomSpecificite
   * @param {string} valeur
   * @returns {{texte: string, icone: string}}
   */
  recupererSpecificite(nomSpecificite) {
    const mappingSpecificites = {
      parfait: { texte: "", icone: "100" },
      shiny: { texte: "shiny", icone: "shiny" },
      chanceux: { texte: "chanceux", icone: "chanceux" },
    }

    return mappingSpecificites[nomSpecificite] || { texte: nomSpecificite, icone: "" }
  }

  async supprimerPokemon() {
    const optionsPopup = {
      titre: "Confirmer la suppression du Pokémon",
      texte: "Êtes-vous sûr de vouloir supprimer ce pokémon ?",
    }
    const resultat = await PopupBase.ouvrirEtAttendre("p-popup-confirmation", optionsPopup)
    if (resultat) {
      this.dispatchEvent(
        new CustomEvent(EVENEMENT_BOUTON_SUPPRIMER_POKEMON, {
          bubbles: true,
          composed: true,
          detail: { nom: this.nom, id: this.id },
        }),
      )
    }
  }

  modifierPokemon() {
    this.dispatchEvent(
      new CustomEvent(EVENEMENT_BOUTON_MODIFIER_POKEMON, {
        bubbles: true,
        composed: true,
        detail: {
          nom: this.nom,
          id: this.id,
          specificites: this.specificites,
          commentaire: this.commentaire,
        },
      }),
    )
  }
}
customElements.define("p-pokemon", Pokemon)
